import ReactMarkdown from "react-markdown";
import { Link } from "react-router";
import remarkGfm from "remark-gfm";
import { conditionHref } from "../../lib/links";
import type { Observation, Turn } from "../../models";
import { CONDITION_SHORT_NAMES } from "../../models/condition-short-names";
import { displayCode, displayName } from "../../models/conditions";

export function TranscriptPane({
  tenant,
  turns,
  observations,
  selectedObsId,
}: {
  tenant: string;
  turns: Turn[];
  observations: Observation[];
  selectedObsId: string | null;
}) {
  const obsByTurn = new Map<number, Observation[]>();
  for (const o of observations) {
    const list = obsByTurn.get(o.evidence.turn) ?? [];
    list.push(o);
    obsByTurn.set(o.evidence.turn, list);
  }

  return (
    <div className="transcript">
      {turns.map((t) => {
        const obsHere = obsByTurn.get(t.index) ?? [];
        const hasSelected = selectedObsId && obsHere.some((o) => o.id === selectedObsId);
        return (
          <div key={t.index} id={`turn-${t.index}`} className={`turn turn-${t.speaker}`}>
            <span className="turn-num">#{t.index}</span>
            <span className="turn-speaker">{t.speaker}</span>
            <div className={`turn-text ${hasSelected ? "evidence-selected" : ""}`}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                // Block raw HTML and dangerous protocols at the renderer
                // level — react-markdown does this by default; explicit for clarity.
                disallowedElements={["script", "style", "iframe"]}
                skipHtml
              >
                {t.text}
              </ReactMarkdown>
              {obsHere.length > 0 ? (
                <div className="evidence-pills">
                  {obsHere.map((o) => {
                    const code = displayCode(o);
                    const short =
                      (o.condition_code && CONDITION_SHORT_NAMES[o.condition_code]) ?? code;
                    return (
                      <Link
                        key={o.id}
                        to={conditionHref(tenant, code)}
                        title={`${displayName(o)} · ${code}`}
                        className={`pill cat-${categoryFor(o.condition_code)} sev-${o.severity} ${selectedObsId === o.id ? "selected" : ""}`}
                      >
                        {short}
                      </Link>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function categoryFor(code: string | undefined): string {
  if (!code) return "other";
  return code[0].toLowerCase();
}
